import { useEffect, useState } from 'react';
import { CpmBadge, CpmButton, CpmCheckbox } from '../../../components/design-system';
import { listGenerators } from '../../../services/reportsAdminService';
import type { GeneratorOption } from '../../../types/reportsAdmin.types';
import { REPORTS_COPY, REPORTS_TIPS } from '../copy';
import CopyIdButton from '../components/CopyIdButton';
import InfoI from '../components/InfoI';
import { ReportsFieldLabel, ReportsWait } from '../components/ReportsDialog';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { pushDropdownCloser } from '../../../utils/dropdownEscapeStack';

interface GeneratorBindSelectProps {
    value: string | null;
    onChange: (reportID: string | null) => void;
    disabled?: boolean;
    confirmNew?: boolean;
    onConfirmNew?: () => void;
}

export default function GeneratorBindSelect({
    value,
    onChange,
    disabled = false,
    confirmNew = false,
    onConfirmNew,
}: GeneratorBindSelectProps) {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState('');
    const debounced = useDebouncedValue(q, 250);
    const [items, setItems] = useState<GeneratorOption[]>([]);
    const [loadState, setLoadState] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');

    const load = async () => {
        setLoadState('loading');
        try {
            setItems(await listGenerators(debounced));
            setLoadState('ready');
        } catch {
            setLoadState('error');
        }
    };

    useEffect(() => {
        if (!open && !value) return;
        void load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, debounced, value]);

    useEffect(() => {
        if (!open) return;
        return pushDropdownCloser(() => setOpen(false));
    }, [open]);

    const selected = items.find((item) => item.reportID === value);

    return (
        <div style={{ minWidth: 0, width: '100%' }}>
            <div className="flex items-center flex-wrap" style={{ gap: 7, minWidth: 0 }}>
                <ReportsFieldLabel hint="Link or unlink the file that builds it">
                    {REPORTS_COPY.WHICH_FILE}
                </ReportsFieldLabel>
                <span className="inline-flex shrink-0" style={{ marginBottom: 5 }}>
                    <InfoI
                        title={REPORTS_TIPS.WHICH_FILE.title}
                        body={REPORTS_TIPS.WHICH_FILE.body}
                    />
                </span>
            </div>
            <div
                style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    border: '1.5px solid var(--cpm-border-strong)',
                    borderRadius: 10,
                    padding: '11px 13px',
                }}
            >
                {value ? (
                    <div>
                        <div className="flex items-center justify-between" style={{ gap: 10 }}>
                            <CpmBadge tone="success" dot>
                                {REPORTS_COPY.CAN_BUILD}
                            </CpmBadge>
                            <CpmButton
                                variant="ghost"
                                size="sm"
                                disabled={disabled}
                                onClick={() => onChange(null)}
                            >
                                {REPORTS_COPY.UNLINK}
                            </CpmButton>
                        </div>
                        <div
                            style={{
                                marginTop: 7,
                                fontFamily: 'var(--cpm-font-mono)',
                                fontSize: 11,
                                fontWeight: 700,
                                color: 'var(--cpm-text-secondary)',
                                wordBreak: 'break-all',
                            }}
                        >
                            {selected?.label ? `${selected.label} · ${value}` : value}
                        </div>
                    </div>
                ) : (
                    <div>
                        <div style={{ fontSize: 12.5, fontWeight: 800 }}>
                            {REPORTS_COPY.CANNOT_BUILD}
                        </div>
                        <div
                            style={{
                                marginTop: 3,
                                fontSize: 11.5,
                                fontWeight: 600,
                                lineHeight: 1.45,
                                color: 'var(--cpm-text-secondary)',
                            }}
                        >
                            Link the id of a builder that already exists. Do not type a fresh id for
                            a report that already runs.
                        </div>
                        <div className="relative" style={{ marginTop: 10 }}>
                            <CpmButton
                                variant="outline"
                                size="sm"
                                disabled={disabled}
                                onClick={() => setOpen((v) => !v)}
                            >
                                {REPORTS_COPY.LINK_BUILDER}
                            </CpmButton>
                            {open ? (
                                <div
                                    className="bg-white"
                                    style={{
                                        position: 'absolute',
                                        top: 42,
                                        right: 0,
                                        zIndex: 120,
                                        width: 384,
                                        maxWidth: 'min(384px, 70vw)',
                                        padding: 10,
                                        border: '1px solid var(--cpm-border-default)',
                                        borderRadius: 12,
                                        boxShadow: 'var(--cpm-shadow-dropdown)',
                                    }}
                                >
                                    <input
                                        value={q}
                                        onChange={(e) => setQ(e.target.value)}
                                        placeholder="Search a builder by name"
                                        style={{
                                            width: '100%',
                                            boxSizing: 'border-box',
                                            height: 38,
                                            padding: '0 12px',
                                            borderRadius: 8,
                                            border: '1.5px solid var(--cpm-border-strong)',
                                            fontFamily: 'var(--cpm-font-sans)',
                                            fontSize: 13,
                                            fontWeight: 600,
                                            outline: 'none',
                                        }}
                                    />
                                    <div
                                        style={{
                                            padding: '8px 4px 6px',
                                            fontSize: 11.5,
                                            fontWeight: 600,
                                            color: 'var(--cpm-text-secondary)',
                                        }}
                                    >
                                        {loadState === 'ready'
                                            ? `${items.length} builders`
                                            : 'Search the builders that already exist'}
                                    </div>
                                    {loadState === 'loading' ? (
                                        <div>
                                            <div style={{ padding: '0 4px 10px' }}>
                                                <ReportsWait label="Loading builders…" />
                                            </div>
                                            <div
                                                className="flex flex-col"
                                                style={{ gap: 9, padding: '0 4px 4px' }}
                                            >
                                                <div className="cpm-sk" style={{ height: 30 }} />
                                                <div className="cpm-sk" style={{ height: 30 }} />
                                                <div
                                                    className="cpm-sk"
                                                    style={{ height: 30, width: '82%' }}
                                                />
                                            </div>
                                        </div>
                                    ) : null}
                                    {loadState === 'error' ? (
                                        <div style={{ padding: '2px 4px 4px' }}>
                                            <div
                                                style={{
                                                    fontSize: 12.5,
                                                    fontWeight: 800,
                                                    color: 'var(--cpm-shortfall-text)',
                                                }}
                                            >
                                                Couldn&apos;t load builders
                                            </div>
                                            <div
                                                style={{
                                                    marginTop: 4,
                                                    fontSize: 11.5,
                                                    fontWeight: 600,
                                                    lineHeight: 1.45,
                                                    color: 'var(--cpm-text-secondary)',
                                                }}
                                            >
                                                The list did not come back, so this says nothing
                                                about how many builders exist. Nothing has been
                                                linked, and the rest of this form is untouched.
                                            </div>
                                            <div style={{ marginTop: 10 }}>
                                                <CpmButton
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => void load()}
                                                >
                                                    Retry the builders
                                                </CpmButton>
                                            </div>
                                        </div>
                                    ) : null}
                                    {loadState === 'ready' && items.length === 0 ? (
                                        <div style={{ padding: '4px 4px 6px' }}>
                                            <div style={{ fontSize: 12.5, fontWeight: 800 }}>
                                                No builder matches that search
                                            </div>
                                            <div
                                                style={{
                                                    marginTop: 3,
                                                    fontSize: 11.5,
                                                    fontWeight: 600,
                                                    color: 'var(--cpm-text-secondary)',
                                                }}
                                            >
                                                Try another name. This is not a system with no
                                                builders.
                                            </div>
                                        </div>
                                    ) : null}
                                    {loadState === 'ready' && items.length > 0 ? (
                                        <div
                                            className="overflow-y-auto [overscroll-behavior:contain]"
                                            style={{
                                                maxHeight: 236,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 2,
                                            }}
                                        >
                                            {items.map((item) => {
                                                const taken = item.status === 'ALREADY_USED';
                                                return (
                                                    <button
                                                        key={item.reportID}
                                                        type="button"
                                                        disabled={taken}
                                                        onClick={() => {
                                                            onChange(item.reportID);
                                                            setOpen(false);
                                                        }}
                                                        className="flex w-full items-center justify-between text-left"
                                                        style={{
                                                            gap: 10,
                                                            padding: '8px 9px',
                                                            borderRadius: 8,
                                                            border: 0,
                                                            background: 'transparent',
                                                            cursor: taken
                                                                ? 'not-allowed'
                                                                : 'pointer',
                                                            opacity: taken ? 0.55 : 1,
                                                        }}
                                                    >
                                                        <span className="min-w-0">
                                                            <span
                                                                className="block"
                                                                style={{
                                                                    fontSize: 12.5,
                                                                    fontWeight: 800,
                                                                    color: taken
                                                                        ? 'var(--cpm-text-secondary)'
                                                                        : 'var(--cpm-text-primary)',
                                                                }}
                                                            >
                                                                {item.label}
                                                            </span>
                                                            <span
                                                                className="block"
                                                                style={{
                                                                    marginTop: 2,
                                                                    fontFamily:
                                                                        'var(--cpm-font-mono)',
                                                                    fontSize: 10,
                                                                    fontWeight: 700,
                                                                    color: 'var(--cpm-text-tertiary)',
                                                                }}
                                                            >
                                                                {item.reportID}
                                                            </span>
                                                        </span>
                                                        <span
                                                            className="flex items-center shrink-0"
                                                            style={{ gap: 7 }}
                                                        >
                                                            <CopyIdButton id={item.reportID} />
                                                            <CpmBadge
                                                                tone={taken ? 'warning' : 'success'}
                                                            >
                                                                {taken
                                                                    ? 'Already used by another report'
                                                                    : 'Available'}
                                                            </CpmBadge>
                                                        </span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    ) : null}
                                </div>
                            ) : null}
                        </div>
                        {onConfirmNew ? (
                            <div
                                style={{
                                    marginTop: 10,
                                    paddingTop: 10,
                                    borderTop: '1px solid var(--cpm-border-default)',
                                }}
                            >
                                <CpmCheckbox
                                    checked={confirmNew}
                                    disabled={disabled}
                                    onChange={onConfirmNew}
                                    label="This is a new id with no builder yet"
                                />
                            </div>
                        ) : null}
                    </div>
                )}
            </div>
        </div>
    );
}
